import api from "../../../../services/api";
import logger from "../../../../utils/logger";
import { Eventbus } from "../../../../utils";
import { events } from "../../../../utils/constants";
import { mergeCollection } from "../../../../utils/helpers";
// TransactionsService - Simple service that emits events via Eventbus
const TransactionsService = (function () {
    const cache = new Map(); // bankFileId -> { data: [], isLoading: false, promise: null }
    let isListenerRegistered = false;

    function ensureEntry(id) {
        if (!cache.has(id)) {
            cache.set(id, { data: [], isLoading: false, promise: null });
        }
        return cache.get(id);
    }

    function getData(bankFileId) {
        if (!bankFileId) return [];
        const entry = ensureEntry(bankFileId);
        return Array.isArray(entry.data) ? [...entry.data] : [];
    }

    function getLoadingState(bankFileId) {
        if (!bankFileId) return false;
        const entry = ensureEntry(bankFileId);
        return !!entry.isLoading;
    }

    function updateTransactionRecord(updated) {
        // Support receiving either a single transaction or an array of transactions
        const items = Array.isArray(updated) ? updated : [updated];
        const updatedEntries = new Set();

        items.forEach((tx) => {
            // try to locate a bankFileId on the incoming payload
            const bankFileId = tx?.bankFileId;
            if (!bankFileId) return;

            const entry = cache.get(bankFileId);
            if (!entry) return;

            if (!Array.isArray(entry.data)) entry.data = [];

            // Use mergeCollection to update the transaction by id, but don't emit the global event
            if (tx.id) {
                // Work on a copy to avoid mutation during concurrent reads
                const updated = [...entry.data];
                mergeCollection([tx], updated, {
                    idField: 'id'
                });
                entry.data = updated;
                
                logger.debug(`Transaction ${tx.id} updated in cache for bankFileId ${bankFileId}`);
                updatedEntries.add(bankFileId);
            }
        });

        // Emit a more specific event for each updated bank file
        for (const bankFileId of updatedEntries) {
            const entry = cache.get(bankFileId);
            if (entry && Array.isArray(entry.data)) {
                // Emit with bankFileId context to allow components to filter appropriately
                Eventbus.emit(events.TRANSACTIONS_UPDATED, {
                    bankFileId,
                    transactions: [...entry.data]
                });
                logger.debug(`Emitted TRANSACTIONS_UPDATED event for bankFileId ${bankFileId}`);
            }
        }
    }

    function handleBankfileMatched() {
        logger.debug('BANKFILE_MATCHED received - refreshing cached transactions');
        // Iterate over known bankFileIds and force a refresh from the API
        for (const bankFileId of cache.keys()) {
            // Force refresh; don't await to avoid blocking the event loop
            loadData(bankFileId, true).catch((err) => {
                logger.error('Error refreshing transactions for', bankFileId, err);
            });
        }
    }

    function loadData(bankFileId, forceRefresh = false) {
        if (!bankFileId) return Promise.resolve([]);

        const entry = ensureEntry(bankFileId);

        // If we're already loading and not forcing refresh, return the existing promise
        if (entry.isLoading && entry.promise && !forceRefresh) {
            return entry.promise;
        }

        // If we have cached data and not forcing refresh, return it and emit event
        // Include bankFileId context so listeners for other bank files ignore it.
        if (entry.data && entry.data.length > 0 && !forceRefresh) {
            Eventbus.emit(events.TRANSACTIONS_LOADED, {
                bankFileId,
                transactions: [...entry.data]
            });
            return Promise.resolve([...entry.data]);
        }

        entry.isLoading = true;

        // Create and store the promise
        entry.promise = api
            .getTransactions(bankFileId)
            .then((response) => {
                if (response && response.code === "ERR_CANCELED") {
                    return [...entry.data];
                }
                entry.data = Array.isArray(response) ? response : (response?.data || []);
                logger.debug(`Transactions loaded for ${bankFileId}:`, entry.data.length);

                // Emit the loaded data via Eventbus with bankFileId context so
                // many listeners (one per expanded row) can ignore unrelated loads.
                Eventbus.emit(events.TRANSACTIONS_LOADED, {
                    bankFileId,
                    transactions: [...entry.data]
                });

                // Only register SignalR listeners once
                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.TRANSACTION_DATA_CHANGES, updateTransactionRecord);
                    // Listen for bank-file match completion so we can reload transactions
                    Eventbus.on(events.BANKFILE_MATCHED, handleBankfileMatched);
                    isListenerRegistered = true;
                    logger.debug('SignalR transaction data changes listener registered');
                }

                return [...entry.data];
            })
            .catch((err) => {
                logger.error("Error fetching transactions for", bankFileId, err);
                entry.data = [];
                // Emit empty array with bankFileId context on error so listeners
                // can handle loading state correctly without overwriting other rows.
                Eventbus.emit(events.TRANSACTIONS_LOADED, {
                    bankFileId,
                    transactions: []
                });
                return [];
            })
            .finally(() => {
                entry.isLoading = false;
                entry.promise = null;
            });

        return entry.promise;
    }

    function searchChildren(searchTerm, maxResults) {
        return api.searchChildren(searchTerm.trim(), maxResults);
    }

    function createMatch(transaction) {
        if (!transaction) {
            return Promise.reject(new Error("Transaction is required"));
        }

        if (!transaction.childId) {
            return Promise.reject(new Error("Child ID is required"));
        }

        logger.debug("Creating match for transaction:", transaction.id, "with child:", transaction.childId);

        return api.updateTransaction(transaction)
            .then((response) => {
                logger.debug("Match created successfully");
                return response;
            })
            .catch((error) => {
                logger.error("Error creating match:", error);
                throw error;
            });
    }

    function ignoreTransaction(transactionId, reasonId) {
        if (!transactionId) {
            return Promise.reject(new Error("Transaction ID is required"));
        }
        if (!reasonId) {
            return Promise.reject(new Error("Reason ID is required"));
        }
        logger.info("Ignoring transaction:", transactionId, "with reason:", reasonId);
        return api.ignoreTransaction(transactionId, reasonId);
    }

    function updateExportType(transactionId, fileTypeId) {
        if (!transactionId) {
            return Promise.reject(new Error("Transaction ID is required"));
        }
        if (!fileTypeId) {
            return Promise.reject(new Error("File Type ID is required"));
        }
        logger.info("Updating export type for transaction:", transactionId, "with file type:", fileTypeId);
        return api.updateExportType(transactionId, fileTypeId);
    }

    function updateTransactionMcot(transaction) {
        if (!transaction) {
            return Promise.reject(new Error("Transaction is required"));
        }
        if (!transaction.id) {
            return Promise.reject(new Error("Transaction ID is required"));
        }
        logger.debug("Updating MCOT transaction:", transaction.id);
        return api.updateTransactionMcot(transaction);
    }

    function createTransaction(transactionData) {
        if (!transactionData) {
            return Promise.reject(new Error("Transaction data is required"));
        }
        if (!transactionData.bankFileId) {
            return Promise.reject(new Error("Bank File ID is required"));
        }
        logger.debug("Creating new transaction for bankFile:", transactionData.bankFileId);
        
        return api.createTransaction(transactionData)
            .then((response) => {
                // After successful creation, force refresh the transactions for this bank file
                // This ensures the new transaction appears immediately in the UI
                const bankFileId = transactionData.bankFileId;
                logger.debug("Transaction created successfully, refreshing data for bankFile:", bankFileId);
                
                // Force refresh to get the latest data including the new transaction
                return loadData(bankFileId, true).then(() => {
                    return response;
                });
            });
    }

    function deleteTransaction(transactionId, bankFileId) {
        if (!transactionId) {
            return Promise.reject(new Error("Transaction ID is required"));
        }
        if (!bankFileId) {
            return Promise.reject(new Error("Bank File ID is required"));
        }
        logger.debug("Deleting transaction:", transactionId, "from bankFile:", bankFileId);
        
        return api.deleteTransaction(transactionId)
            .then((response) => {
                // After successful deletion, force refresh the transactions for this bank file
                // This ensures the transaction is removed immediately from the UI
                logger.debug("Transaction deleted successfully, refreshing data for bankFile:", bankFileId);
                
                // Force refresh to get the latest data without the deleted transaction
                return loadData(bankFileId, true).then(() => {
                    return response;
                });
            });
    }

    function splitTransaction(splitData) {

        if(!splitData){
            return Promise.reject(new Error("Split data is required"));
        }
        logger.info("Placeholder: Split transaction API call with data:", splitData);
       
        return api.updateTransactionSplit(splitData.transactionId, splitData.splits);
    }

    function getTransactionSplits(transactionId) {
        if (!transactionId) {
            return Promise.reject(new Error("Transaction ID is required"));
        }
        return api.getTransactionSplits(transactionId);
    }

    function deleteTransactionSplits(transactionId) {
        if (!transactionId) {
            return Promise.reject(new Error("Transaction ID is required"));
        }
        return api.deleteTransactionSplit(transactionId);
    }

    function updateFlag(flag) {
        if (!flag) {
            return Promise.reject(new Error("Flag is required"));
        }
        if (!flag.id) {
            return Promise.reject(new Error("Flag ID is required"));
        }
        logger.info("Updating flag:", flag.id, "to ignore:", flag.ignore);
        return api.updateFlag(flag);
    }

    function clear(bankFileId) {
        if (bankFileId) {
            cache.delete(bankFileId);
        } else {
            if (isListenerRegistered) {
                Eventbus.off(updateTransactionRecord);
                Eventbus.off(handleBankfileMatched);
                isListenerRegistered = false;
                logger.debug('SignalR transaction data changes listener removed');
            }
            cache.clear();
        }
    }

    return {
        loadData,
        getData,
        clear,
        getLoadingState,
        searchChildren,
        createMatch,
        splitTransaction,
        getTransactionSplits,
        deleteTransactionSplits,
        ignoreTransaction,
        updateExportType,
        updateTransactionMcot,
        createTransaction,
        deleteTransaction,
        updateFlag,
    };
})();

export default TransactionsService;
